const MVCObject = require("./mvcobject").MVCObject;

/**
 * @typedef {Object} TextSearchQuery
 * @property {String} [value] the text to be searched.
 * @property {Object} [options] keys are the attributes of the store where the search will be performed,
 * attached value are operator that will be used. Attributes can be `name`, `city`, and `idstore`.
 * Operators can be `SearchQuery.AND`, and `SearchQuery.OR`
 * @example Example value
 * ```javascript
 * var textSearchQuery = {
 *   'value': 'string to search',
 *   'options': {
 *     'name': SearchQuery.OR,
 *     'city': SearchQuery.AND
 *   }
 * }
 * ```
 * @memberOf woosmap
 */

/**
 * @typedef {Object} QuerySearchOptions
 * @property {woosmap.TextSearchQuery} [query] search string and its options.
 * @property {Array.<String>} [tags] a list of tags object
 * @property {Array.<String>} [types] a list of types object
 * @example Example value
 * ```javascript
 * var querySearchOptions = {
 *   'query': {
 *     'value': 'string to search',
 *     'options': {
 *       'name': SearchQuery.OR,
 *       'city': SearchQuery.AND
 *     }
 *   },
 *   'tags': [{
 *     'value': 'a tag name',
 *     'operator': SearchQuery.OR
 *   }],
 *   'types': [{
 *     'value': 'a type name', 'operator': SearchQuery.AND
 *   }]
 * }
 * ```
 * @memberOf woosmap
 */

/**
 * @deprecated
 * @memberOf woosmap.search
 * @extends woosmap.utils.MVCObject
 */
class SearchQuery extends MVCObject {
    /**
     * Constructs a new SearchQuery
     * @param {woosmap.QuerySearchOptions} querySearchOptions
     * @constructor
     * @deprecated
     */
    constructor(querySearchOptions) {
        super();
        const params = querySearchOptions || {};
        this.text = "";

        /**
         * Property for text search
         * @type {woosmap.TextSearchQuery}
         */
        this.query = params.query || { value: "", options: { name: SearchQuery.OR } };
        /**
         * Property for tag search
         * @type {Array.<String>}
         */
        this.tags = params.tags || [];
        /**
         * Property for type search
         * @type {Array.<String>}
         */
        this.types = params.types || [];
    }

    text_changed() {
        this.setQuery(this.get("text"));
    }

    /**
     * Generic addFilter
     * @param {Array.<String>} to this.tags or this.types
     * @param {String} name the name of the tag or the type
     * @param {Boolean} operator the boolean operator to use in the query.
     * @private
     */
    _addFilter(to, name, operator) {
        const _operator = operator || SearchQuery.AND;
        to.push({ value: name, operator: _operator });
    }

    /**
     * Adds a tag filter to the query.
     * @param {String} tagName the tag name
     * @param {Boolean} operator the boolean operator to use in the query.
     */
    addTagFilter(tagName, operator) {
        this._addFilter(this.tags, tagName, operator);
    }

    /**
     * Adds a type filter to the query.
     * @param {String} typeName the type name
     * @param {Boolean} operator the boolean operator to use in the query.
     */
    addTypeFilter(typeName, operator) {
        this._addFilter(this.types, typeName, operator);
    }

    /**
     * Sets the query text
     * @param {String} queryText the query text
     * @param {Object} [options]
     */
    setQuery(queryText, options) {
        this.query.value = queryText;
        if (options) {
            this.query.options = options;
        }
    }

    /**
     * Adds a query option
     * @param {String} name the option to add
     * @param {Boolean} operator the boolean operator to use in the query.
     */
    addQueryOption(name, operator) {
        this.query.options[name] = operator;
    }

    /**
     * Removes a QueryOption by name.
     * @param {String} name
     */
    removeQueryOption(name) {
        delete this.query.options[name];
    }

    /**
     * Merge query, tag and type Properties into a query string
     * @returns {string} querystring the string constructed with the params
     * @private
     */
    stringify() {
        function listToStringParam(paramList, type, searchParam) {
            for (let i = 0; i < paramList.length; i++) {
                searchParam.push(`${paramList[i].operator + type}:"${paramList[i].value}"`);
            }
        }

        const searchParam = [];
        if (this.query?.value) {
            const query = this.query;
            const queryValue = query.value;
            for (const optionName in query.options) {
                if (query.options.hasOwnProperty(optionName)) {
                    searchParam.push(`${query.options[optionName] + optionName}:"${queryValue}"`);
                }
            }
        }
        if (this.tags) {
            listToStringParam(this.tags, "tag", searchParam);
        }
        if (this.types) {
            listToStringParam(this.types, "type", searchParam);
        }
        return searchParam.join(" ");
    }
}

/**
 * AND query operator
 * @type {string}
 */
SearchQuery.AND = "&";

/**
 * OR query operator
 * @type {string}
 */
SearchQuery.OR = "|";

exports.SearchQuery = SearchQuery;
